// react
// dependencies
import React, { useEffect, useMemo, useRef, useState } from "react";

// components
import Row from "../components/Row";
import TableEmpty from "../components/TableEmpty";
import TableHead from "../components/TableHead";
import TableLoading from "../components/TableLoading";

// hooks
import useWindowSize from "../../../hooks/useWindowSize";

// utils
import get from "../../../utils/GetNested";

// style
import css from "../Table.module.scss";

/* This component is a table that can scroll horizontally
 it works similarly to TableLight, but has a few differences:
 - it can scroll horizontally
 - it can have sticky columns using the isSticky property
 - column widths must be in number of pixels

 We achieve the sticky columns by rendering two tables, one that scrolls and one that is sticky
 The sticky table is rendered on top of the scrollable table and has a backdrop that matches the background color of the table
 We calculate the row hights (including the header row) from the scrollable table and pass them to the sticky table

 Currently the original table's columns all still render behind the sticky table
 */

export declare interface ScrollableColumn {
	id: string;
	label: string;
	sortName?: string;
	isSortable?: boolean;
	width?: number;
	isSticky?: boolean;
	mobileViewHideColumn?: number;
	getter?: (_row: any) => any;
}

export interface ColumnGroup {
	isSortable: boolean;
	width: any;
	stickyColumnIDs: string[];
	subColumns: string[];
	id: string;
	label: string;
	columnSpan: number;
	stickyColumnIds: string[];
}
interface Subheader {
	mobileViewHideColumn?: number;
	width?: number;
}

type SubheaderValues = Subheader[];
export interface ScrollableTableProps {
	/**
	 * row data that maps onto columns
	 */
	rows: Array<any> | null;
	/**
	 * columns in table, expects an array of ScrollableColumns
	 */
	columns: Array<ScrollableColumn> | null;
	/**
	 * column subheaders
	 */
	subheaders?: Subheader | null;
	/**
	 * active sort column id
	 */
	activeSort?: string | null;
	/**
	 * active sort direction, typically asc | desc
	 */
	activeSortDirection?: string | null;
	/**
	 * function to call when a column is clicked
	 */
	onColumnClick?: (columnID: string, nextDirection: string) => void;
	/**
	 * class to apply to body cells
	 */
	bodyCellClass?: string | null;
	/**
	 * class to apply to header cells
	 */
	headCellClass?: string | null;
	/**
	 * is the table loading
	 */
	isLoading?: boolean;
	/**
	 * row key
	 */
	rowKey?: string;
	/**
	 * text to display when table is empty
	 * @deprecated use emptyChildren instead
	 */
	emptyText?: string | null;
	/**
	 * children to display when table is empty
	 */
	emptyChildren?: React.ReactNode;

	/** should the table have rounded top corners
	 */
	roundedTopCorners?: boolean;
	/**
	 * column groups for column headers
	 */
	columnGroups?: ColumnGroup[];
	/**
	 * Row class
	 */
	rowClassName?: string | null;
}

const tablePadding = 8;
const horizontalPadding = 16;

/** @deprecated Use `Table` from `@performyard/py-components` instead. */
const ScrollableTable = ({
	rows = [],
	columns = [],
	subheaders = {},
	activeSort = null,
	activeSortDirection = null,
	onColumnClick = () => {},
	bodyCellClass = null,
	headCellClass = null,
	isLoading = false,
	rowKey = "id",
	emptyText = null,
	emptyChildren = null,
	roundedTopCorners = true,
	columnGroups = [],
	rowClassName = null,
}: ScrollableTableProps) => {
	const [scrollPosition, setScrollPosition] = useState(0);

	const tableRef = useRef<HTMLDivElement | null>(null);
	const tableHeadRef = useRef<HTMLDivElement | null>(null);
	const tableRowRef = useRef<HTMLDivElement[]>([]);
	const tableScrollContainer = useRef<HTMLDivElement>(null);

	const tableColumnRefs = useRef<HTMLDivElement[]>([]);
	const stickyColumnRefs = useRef<HTMLDivElement[]>([]);

	useEffect(() => {
		tableColumnRefs.current = tableColumnRefs.current.slice(0, columns?.length);
	}, [JSON.stringify(columns)]);

	let columnRefWidths: number[] = [];
	let stickyColumnRefWidths: number[] = [];

	if (tableColumnRefs?.current?.length) {
		// get width of columns
		columnRefWidths = tableColumnRefs.current.map((column) => column?.offsetWidth);
	}

	if (stickyColumnRefs?.current?.length) {
		// get width of sticky columns
		stickyColumnRefWidths = stickyColumnRefs.current.map((column) => column?.offsetWidth);
	}

	const [width] = useWindowSize();
	const [colWidths, subheaderColWidths] = useMemo(() => {
		if (columns?.length) {
			// set column widths based on screen size
			const currentColWidths = columns?.map((col) => {
				if (!col?.mobileViewHideColumn || col?.mobileViewHideColumn < width) {
					return get(col, ["width"], 100);
				}
				return "";
			});

			// set subheader column widths based on screen size
			let subheaderItems;
			if (subheaders) {
				const subheaderValues: SubheaderValues[] = Object.values(subheaders);

				subheaderItems = [];
				subheaderValues.forEach((group: Subheader[]) => {
					group?.forEach((subheader) => {
						if (!subheader?.mobileViewHideColumn || subheader?.mobileViewHideColumn < width) {
							const item = get(subheader, ["width"], 100);
							subheaderItems.push(item);
						}
					});
				});
			}

			const subheaderWidths = subheaderItems;

			return [currentColWidths, subheaderWidths];
		}
		return [""];
	}, [width, columns]);

	// map column groups to include width, sticky columns, and subcolumns
	// column groups appear as top header columns
	let currentColumn = 0;
	const mappedColumnGroups = columnGroups.map((group) => {
		const widthOfColumns = Array.isArray(colWidths)
			? colWidths.slice(currentColumn, currentColumn + group.columnSpan).reduce((a, b) => a + b, 0)
			: 0;
		if (columns) {
			const subColumns = columns.slice(currentColumn, currentColumn + group.columnSpan);
			const stickyColumnIDs = subColumns.filter((column) => column.isSticky).map((column) => column.id);

			currentColumn += group.columnSpan;

			return {
				...group,
				isSortable: false,
				width: widthOfColumns + (subColumns.length - 1) * tablePadding, // 8 is the padding between columns (4px on each side)
				stickyColumnIDs,
				subColumns: subColumns.map((column) => column.id),
			};
		}
	});

	const handleColumnClick = (columnID, nextDirection) => {
		onColumnClick(columnID, nextDirection);
	};

	const getTableContent = (tableColumns, tableColumnWidths, isScrollable) => {
		let tableContent;

		if (isLoading) {
			tableContent = (
				<TableLoading
					columnWidths={convertWidthsToPixels(tableColumnWidths)}
					cols={tableColumns.length}
					rows={20}
					scrollable={true}
				/>
			);
		} else if (rows?.length === 0) {
			tableContent = null;
			tableContent = <TableEmpty emptyChildren={emptyChildren} emptyText={emptyText ? emptyText : ""} />;
		} else {
			tableContent = (
				<div data-testid="tableLiteBody" className={css.tableBody}>
					{rows?.map((row, index) => (
						<div
							key={`scrollableTable-${index}-${isScrollable ? "" : "sticky"}`}
							ref={(element) => {
								if (isScrollable) {
									tableRowRef.current[index] = element!;
								}
							}}
							className={`${css.stickyTableRowWrapper} ${index === rows.length - 1 && css.lastRow}`}
						>
							<Row
								key={rowKey ? row[rowKey] : index}
								row={row}
								last={index === rows.length - 1}
								columns={tableColumns}
								subheaders={subheaders}
								colWidths={convertWidthsToPixels(tableColumnWidths)}
								subheaderColWidths={convertWidthsToPixels(subheaderColWidths)}
								bodyCellClass={bodyCellClass}
								defaultCellClassName={headCellClass}
								currentScreenWidth={width}
								clientHeight={!isScrollable ? tableRowRef.current[index]?.clientHeight - 16 : 0}
								columnRefWidths={isScrollable ? columnRefWidths : stickyColumnRefWidths}
								rowClassName={rowClassName}
							/>
						</div>
					))}
				</div>
			);
		}

		return tableContent;
	};

	// Here we calculate which columns are sticky based on the scroll position and column widths
	// if the scroll position is greater than the sum of the widths of all columns to the left of the current column
	// and the column has isSticky = true then the column is sticky
	const stickyColumns: ScrollableColumn[] = [];
	const stickyColumnWidths: number[] = [];
	const stickyColumnGroups: ColumnGroup[] = [];

	columns?.forEach((column, index) => {
		let currentColumnStartWidths;
		let currentStickyColumnOffset;
		if (Array.isArray(colWidths)) {
			currentColumnStartWidths = colWidths.slice(0, index).reduce((a, b) => a + b, 0);
			currentStickyColumnOffset =
				stickyColumnWidths.length > 0 ? stickyColumnWidths.reduce((a, b) => a + b, 0) - tablePadding : 0;
		} else if (typeof colWidths === "string") {
			currentColumnStartWidths = Number(colWidths);
			currentStickyColumnOffset = Number(colWidths) - tablePadding;
		}

		const columnsGroup = mappedColumnGroups.find((group) => group?.subColumns?.includes(column.id));
		const doesColumnsGroupAlreadyHaveStuckColumns = stickyColumns.some((column) =>
			columnsGroup?.stickyColumnIDs?.includes(column.id)
		);

		if (
			column?.isSticky &&
			scrollPosition >= currentColumnStartWidths - currentStickyColumnOffset &&
			scrollPosition > tablePadding
		) {
			stickyColumns.push(column);
			stickyColumnWidths.push(colWidths[index]);
			stickyColumnGroups.push(columnsGroup as ColumnGroup);
		} else if (doesColumnsGroupAlreadyHaveStuckColumns) {
			stickyColumns.push(column);
			stickyColumnWidths.push(colWidths[index]);
		}
	});

	const handleScroll = () => {
		const scrollLeft = tableScrollContainer?.current?.scrollLeft;
		setScrollPosition(scrollLeft !== undefined ? scrollLeft : 0);
	};

	const convertWidthsToPixels = (widths) => {
		return widths
			.map((width) => {
				if (width === "") {
					return width;
				}
				return `${width}px`;
			})
			.join(" ");
	};
	let clientHeight;
	if (tableHeadRef.current) {
		if (columnGroups.length > 0) {
			clientHeight = tableHeadRef.current.clientHeight / 2;
		} else {
			clientHeight = tableHeadRef.current[0]?.clientHeight;
		}
		clientHeight -= 10; // 10 is the padding that's already included in the height
	}

	const allColumnsWidth =
		// sum of all column widths
		(Array.isArray(colWidths)
			? colWidths.reduce((a, b) => a + b + tablePadding, 0)
			: Number(colWidths) + tablePadding) +
		tablePadding + // outside edges of first and last column
		horizontalPadding; // padding on the left and right of the table

	return (
		<div ref={tableRef} className={`${css.tableContainer} ${roundedTopCorners && css.roundedCorners}`}>
			<div className={`${css.scrollableTable}`} onScrollCapture={handleScroll}>
				<div className={css.headerBackdrop} style={{ height: 10 }} />
				<div ref={tableScrollContainer} className={`${css.scrollableTable} ${css.main}`}>
					<div
						style={{
							width: `${Math.max(allColumnsWidth, tableRef.current?.clientWidth || 0)}px`,
						}}
						className={css.tableHeadWrapper}
						ref={tableHeadRef}
					>
						{columnGroups.length > 0 && (
							<TableHead
								columns={mappedColumnGroups}
								colWidths={convertWidthsToPixels(mappedColumnGroups.map((group) => group?.width))}
								subheaders={subheaders}
								activeSort={activeSort}
								onColumnClick={handleColumnClick}
								defaultCellClassName={headCellClass}
								activeSortDirection={activeSortDirection}
								currentScreenWidth={width}
								dividedColumns
								tableColumnRefs={tableColumnRefs}
							/>
						)}
						<TableHead
							columns={columns || []}
							colWidths={convertWidthsToPixels(colWidths)}
							subheaders={subheaders}
							activeSort={activeSort}
							onColumnClick={handleColumnClick}
							defaultCellClassName={headCellClass}
							activeSortDirection={activeSortDirection}
							currentScreenWidth={width}
							tableColumnRefs={tableColumnRefs}
						/>
					</div>

					{getTableContent(columns, colWidths, true)}
				</div>
			</div>
			{stickyColumns.length > 0 && (
				<div className={css.stickyTable}>
					<div className={css.headerBackdrop} style={{ height: tableHeadRef?.current?.clientHeight }} />
					<div className={css.headerBackdrop} style={{ height: 10 }} />
					<div className={`${css.main} ${css.sticky}`}>
						<div className={css.tableHeadWrapper}>
							{columnGroups.length > 0 && (
								<TableHead
									columns={stickyColumnGroups}
									colWidths={convertWidthsToPixels(stickyColumnGroups.map((group) => group.width))}
									subheaders={subheaders}
									activeSort={activeSort}
									onColumnClick={handleColumnClick}
									defaultCellClassName={headCellClass}
									activeSortDirection={activeSortDirection}
									currentScreenWidth={width}
									dividedColumns
									tableColumnRefs={stickyColumnRefs}
								/>
							)}
							<TableHead
								columns={stickyColumns}
								colWidths={convertWidthsToPixels(stickyColumnWidths)}
								subheaders={subheaders}
								activeSort={activeSort}
								onColumnClick={handleColumnClick}
								defaultCellClassName={headCellClass}
								activeSortDirection={activeSortDirection}
								currentScreenWidth={width}
								clientHeight={clientHeight}
								tableColumnRefs={stickyColumnRefs}
							/>
						</div>
						<div
							style={{
								paddingTop: "10px",
							}} /*10 is the top offset needed to realign the table secitons */
						>
							{getTableContent(stickyColumns, stickyColumnWidths, false)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

/** @deprecated Use `Table` from `@performyard/py-components` instead. */
export default ScrollableTable;
